import React, { useState, useRef } from 'react';
import RepliCloudLogo from '../assets/RepliCloudLogo.png';
import RepliCloudLogoDM from '../assets/RepliCloudLogoDM.svg';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/auth';
import { ui } from '../config/uiSizes';
import { useSmartBack } from '../hooks/ui';
import { RepliCloudFAQsModal } from './Modals/FAQ/RepliCloudFAQsModal';
import { SESSION_KEY_REGION } from '../types/AppConfig';
import type { StoredRegion } from '../types/AppConfig';
interface HeaderProps {
  variant?:
  'dashboard'
  | 'capture-snapshot'
  | 'migration-setup'
  | 'approve-transformations'
  | 'approve-export'
  | 'migration-logs'
  | 'snapshot-comparison'
  | 'snapshot-hub'
  | 'rollback-preview'
  | 'rollback-initiation';
}
const Header: React.FC<HeaderProps> = ({ variant = 'dashboard' }) => {
  const navigate = useNavigate();
  const smartBack = useSmartBack();
  const { logout, userInfo } = useAuth();
  const [showFAQs, setShowFAQs] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltipTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showHelpIcon = variant === 'migration-setup' || variant === 'approve-transformations' || variant === 'approve-export';

  // Read the region the user logged in with from sessionStorage
  const storedRegion: StoredRegion | null = (() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY_REGION);
      return raw ? (JSON.parse(raw) as StoredRegion) : null;
    } catch {
      return null;
    }
  })();

  // Only trust the label if it was explicitly set when the user picked a region on the
  // login screen — the loginBaseUri fallback can reflect stale/incorrect session data.
  const regionLabel: string = storedRegion?.label ?? '';
  const regionIsUnknown = !regionLabel;

  const handleTooltipEnter = () => {
    if (tooltipTimeout.current) clearTimeout(tooltipTimeout.current);
    setTooltipOpen(true);
  };
  const handleTooltipLeave = () => {
    tooltipTimeout.current = setTimeout(() => setTooltipOpen(false), 120);
  };

  const getHeaderTitle = () => {
    if (variant === 'dashboard') return '';
    if (variant === 'capture-snapshot') return 'Snapshot Setup';
    if (variant === 'migration-setup') return 'Migration Setup';
    if (variant === 'approve-transformations') return 'Approve Transformations';
    if (variant === 'approve-export') return 'Approve Export';
    if (variant === 'migration-logs') return 'Activity Logs';
    if (variant === 'snapshot-comparison') return 'Snapshot Comparison Tool';
    if (variant === 'snapshot-hub') return 'Snapshot Hub';
    if (variant === 'rollback-preview') return 'Automated Rollback';
    if (variant === 'rollback-initiation') return 'Automated Rollback';
    return '';
  };

  return (
    <div className={`w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800${variant === 'snapshot-comparison' ? ' sticky top-0 z-10' : ''}`}>
      {/* ── Main header row: logo | title | buttons ── */}
      <header className="flex items-center border-b border-gray-200 dark:border-gray-800 px-6 py-3 bg-white dark:bg-gray-900 backdrop-blur">
        {/* Left: logo — flex-1 keeps it anchored left */}
        <div className="flex flex-1 items-center">
          <a
            href="/dashboard"
            onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}
            className="flex items-center cursor-default"
          >
            <img
              src={RepliCloudLogo}
              alt="Genesys Migrator"
              className={`${ui.widthAndHeight.logoHeight} w-auto object-contain dark:hidden`}
            />
            <img
              src={RepliCloudLogoDM}
              alt="Genesys Migrator"
              className={`${ui.widthAndHeight.logoHeight} w-auto object-contain hidden dark:block`}
            />
          </a>
        </div>

        {/* Center: page title — flex-shrink-0 so it never squashes */}
        <div className="flex flex-shrink-0 items-center gap-2">
          <h1 className={`${ui.text.headerPageTitle} font-extrabold text-gray-800 dark:text-gray-100 tracking-tight whitespace-nowrap`}>
            {getHeaderTitle()}
          </h1>
          {showHelpIcon && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowFAQs(true)}
                onMouseEnter={handleTooltipEnter}
                onMouseLeave={handleTooltipLeave}
                className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500 text-white shadow-md transition-all duration-200"
                aria-label="View RepliCloud FAQs"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
                </svg>
              </button>
              {tooltipOpen && (
                <div
                  onMouseEnter={handleTooltipEnter}
                  onMouseLeave={handleTooltipLeave}
                  className="absolute top-full pt-1 left-1/2 -translate-x-1/2 z-50"
                >
                  <div className="w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 whitespace-normal text-center">
                    <p className="mb-2">Have questions about migrations, snapshots, transformations &amp; rollbacks?</p>
                    <button
                      type="button"
                      onClick={() => { setTooltipOpen(false); setShowFAQs(true); }}
                      className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 font-semibold underline hover:text-blue-700 dark:hover:text-blue-300"
                    >
                      View RepliCloud FAQs
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: action buttons — flex-1 + justify-end keeps them anchored right */}
        <div className={`flex flex-1 items-center justify-end ${ui.gap.headerButtons}`}>
          {variant === 'dashboard' ? (
            <>
              <button
                className={`rounded-lg bg-green-600 ${ui.button.header} text-white font-semibold shadow transition hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 whitespace-nowrap`}
                onClick={() => navigate('/CaptureSnapshot')}
              >
                Capture Snapshot
              </button>
              <button
                className={`rounded-lg bg-blue-600 ${ui.button.header} text-white font-semibold shadow transition hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 whitespace-nowrap`}
                onClick={() => navigate('/MigrationSetup')}
              >
                Start Migration
              </button>
              <button
                className={`rounded-lg bg-red-600 ${ui.button.header} text-white font-semibold shadow transition hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 whitespace-nowrap`}
                onClick={() => {
                  logout();
                  navigate('/');
                }}
              >
                Logout
              </button>
            </>
          ) : (
            <button
              className={`rounded-lg bg-blue-600 ${ui.button.header} text-white font-semibold shadow transition hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 whitespace-nowrap`}
              onClick={smartBack}
            >
              {(variant === 'migration-logs' || variant === 'snapshot-comparison' || variant === 'rollback-preview' || variant === 'rollback-initiation') ? '← Back' : 'Back to Dashboard'}
            </button>
          )}
        </div>
      </header>

      {/* ── User info strip — sits below the header row, full width, no truncation ── */}
      {userInfo && (
        <div className={`flex items-center ${ui.gap.sm} ${ui.page.userInfoStrip} bg-gradient-to-r from-gray-50 via-slate-50 to-gray-100 dark:from-gray-800 dark:via-gray-850 dark:to-gray-900 border-b-2 border-gray-300 dark:border-gray-600`}>

          {/* Profile avatar circle */}
          <div className={`flex-shrink-0 ${ui.widthAndHeight.userInfoAvatar} rounded-full bg-gradient-to-br from-gray-600 to-slate-700 dark:from-gray-500 dark:to-slate-600 flex items-center justify-center shadow-lg ring-2 ring-white dark:ring-gray-800`}>
            <svg className={`${ui.svg.xxs} text-white`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
            </svg>
          </div>

          {/* "Logged in as" label + user's full name */}
          <span className={`${ui.text.xs} text-gray-600 dark:text-gray-400 whitespace-nowrap font-medium`}>Logged in as</span>
          <span className={`${ui.text.xs} font-bold text-gray-900 dark:text-white whitespace-nowrap`}>{userInfo.userFullName}</span>

          {/* "from" separator */}
          <span className={`${ui.text.xs} text-gray-600 dark:text-gray-400 whitespace-nowrap font-medium`}>from</span>

          {/* Organisation badge */}
          <div className={`flex items-center ${ui.gap.xs} bg-gray-100 dark:bg-gray-700/40 border-2 border-gray-300 dark:border-gray-600 rounded-full px-3 py-0.5 shadow`}>
            <svg className={`${ui.svg.xxs} text-gray-600 dark:text-gray-400 flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
            </svg>
            <span className={`${ui.text.xs} font-bold text-gray-700 dark:text-gray-200 whitespace-nowrap`}>{userInfo.orgFriendlyName}</span>
          </div>

          {/* Region badge — known region shown in blue; unknown shown as yellow warning */}
          {regionIsUnknown ? (
            <div className={`flex items-center ${ui.gap.xs} bg-yellow-50 dark:bg-yellow-900/30 border-2 border-yellow-400 dark:border-yellow-600 rounded-full px-3 py-0.5 shadow`}>
              <svg className={`${ui.svg.xxs} text-yellow-600 dark:text-yellow-400 flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
              </svg>
              <span className={`${ui.text.xs} font-bold text-yellow-700 dark:text-yellow-300 whitespace-nowrap`}>
                Unknown region — please log out and back in
              </span>
            </div>
          ) : (
            <div className={`flex items-center ${ui.gap.xs} bg-blue-50 dark:bg-blue-950/30 border-2 border-blue-300 dark:border-blue-700 rounded-full px-3 py-0.5 shadow`}>
              <svg className={`${ui.svg.xxs} text-blue-600 dark:text-blue-400 flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253" />
              </svg>
              <span className={`${ui.text.xs} font-bold text-blue-700 dark:text-blue-300 whitespace-nowrap`}>{regionLabel}</span>
            </div>
          )}

        </div>
      )}

      {showHelpIcon && (
        <RepliCloudFAQsModal
          isOpen={showFAQs}
          onClose={() => setShowFAQs(false)}
          onBack={() => setShowFAQs(false)}
        />
      )}
    </div>
  );
};

export default Header;

